import { auth as useAuthStore } from 'src/store/auth';
import { computed, onMounted } from 'vue';

/**
 * The two catalogs a service delivery points into, as `{ id, name }` entries.
 *
 * THIS OWNS THE LOADING, WHICH IS THE POINT. Both catalogs bind lazily on first use, and the flag
 * that records the bind is module-level — so whether they are populated depends on which screen the
 * user visited earlier in the session, not on which screen is asking. A screen that reads them
 * without ensuring either bind shows a full picker or an empty one depending on route history,
 * silently and with no error.
 *
 * IT LIVES HERE RATHER THAN IN THE CONTEXT because places of service and treatment services are
 * clinic configuration still owned by legacy code. A context that read this store would put the app
 * inside its own published interface: `fb/sessionActivities-db` and `boot/globals` both import
 * `src/contexts/treatmentPlanning`, so the barrel would no longer be usable by the layer beneath the
 * store.
 *
 * `{ id, name }` IS THE CONTEXT'S VOCABULARY, NOT THIS STORE'S AND NOT THE PICKER'S. Translating
 * `placeOfServiceName` and `serviceName` is this side's job, because the legacy field names are a
 * legacy concern; turning the result into options is the field group's job, because the option shape
 * belongs to the design system. Neither vocabulary should reach the other.
 *
 * @returns {{
 *   placesOfService: import('vue').ComputedRef<Array<{id: string, name: string}>>,
 *   treatmentServices: import('vue').ComputedRef<Array<{id: string, name: string}>>,
 *   isLoading: import('vue').ComputedRef<boolean>,
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useServiceDeliveryCatalogs() {
  const authStore = useAuthStore();

  onMounted(() => {
    // Both actions short-circuit once bound and share an in-flight promise between concurrent
    // callers, so asking on every mount costs nothing after the first.
    //
    // Swallowed rather than surfaced, and the store has already logged: it rethrows so an awaiting
    // caller can react, and nothing here awaits. A failed bind leaves the picker empty, which is the
    // same thing a clinic that has configured nothing sees, and is better than a mounting field
    // group taking the dialog down with it.
    authStore.ensurePlacesOfServiceBound().catch(() => {});
    authStore.ensureTreatmentServicesBound().catch(() => {});
  });

  const placesOfService = computed(() => (authStore.placesOfService ?? []).map(
    (placeOfService) => ({ id: placeOfService.id, name: placeOfService.placeOfServiceName }),
  ));

  const treatmentServices = computed(() => (authStore.treatmentServices ?? []).map(
    (treatmentService) => ({ id: treatmentService.id, name: treatmentService.serviceName }),
  ));

  const isLoading = computed(
    () => Boolean(authStore.isLoadingPlacesOfService) || Boolean(authStore.isLoadingTreatmentServices),
  );

  return { placesOfService, treatmentServices, isLoading };
}
